/* eslint-disable no-use-before-define */
/* eslint-disable no-await-in-loop */
import clonedeep from 'lodash.clonedeep';
import isequal from 'lodash.isequal';

let infiniteLoop = 0;
let isRestoredWorkflow = null;
/* Returns a cleans version of the workflow with selected and custom products and all flags reset */
const cleanWorkflowSteps = (workflowSteps) =>
	new Promise((resolve) => {
		for (const step of workflowSteps) {
			// ignore custom steps
			if (step?.customContent) continue;

			// remove custom products that are not selected
			step.products = step.products.filter((product) => {
				if (!product.key.includes('custom-product')) return true;
				if (step.selected.find((sel) => sel.initial === product.key)) return true;
				return false;
			});

			// reset all flags
			for (const {rules} of step?.products) {
				for (const rule in rules) if (rule) rules[rule].clear();
			}
		}
		resolve(workflowSteps);
	});

const updateTempRules =
	({dispatch, getters}) =>
	async (workflowSteps) =>
		dispatch('setTempRules', await getRules(await getters.getHcubeRules, workflowSteps));

/** Checks if workflow has new rules */
const checkWorkflowForRules =
	({dispatch, getters}) =>
	async (workflowSteps) => {
		const currentRuleKeys = await getters.getSelectedProductWithRules(workflowSteps);
		const doTheyMatch = isequal(getters.getRulesInPlay, currentRuleKeys);
		if (!doTheyMatch) dispatch('setRulesInPlay', currentRuleKeys);
		return doTheyMatch;
	};

/** Checks workflow as a whole and decides how many times rules are run */
const runRulesForWorkflow = (veuxArgs) => async (wfs) => {
	let workflowSteps = wfs;
	const {getters, rootGetters} = veuxArgs;

	// set isRestoredWorkflow flag
	isRestoredWorkflow = rootGetters['restoreWorkflow/isRestoredWorkflow'];

	// run rules if needed
	while (!(await checkWorkflowForRules(veuxArgs)(workflowSteps)) && infiniteLoop < 100) {
		// update loop guard
		++infiniteLoop;

		// clean workflowsteps
		workflowSteps = await cleanWorkflowSteps(workflowSteps);

		// update temp rules
		await updateTempRules(veuxArgs)(workflowSteps);

		// get temp rules
		const currentRules = await getters.getTempRules;
		// run each group of rules through all workflowSteps

		// run all exculde rules first
		for (const ruleName of Object.keys(currentRules))
			workflowSteps = await runRulesForAllSteps(veuxArgs)(ruleName, workflowSteps, 'excludeHasRun');
		// run all other rules
		for (const ruleName of Object.keys(currentRules))
			workflowSteps = await runRulesForAllSteps(veuxArgs)(ruleName, workflowSteps);
	}

	infiniteLoop = 0;
	return workflowSteps;
};

/* Runs a rule group through all steps in given work • Returns modified workflow */
const runRulesForAllSteps =
	(veuxArgs) =>
	async (stepName, allSteps, ruleType = 'ruleHasRun') => {
		const {getters} = veuxArgs;

		// all steps in workflow
		let $allSteps = allSteps;

		// current step with all selected product rules
		const ruleGroup = await getters.getStepRule(stepName);
		for (const rule of Object.keys(ruleGroup)) {
			// ignore if rule has already ran
			if (ruleGroup[rule]?.[ruleType] === true) continue;

			// check if rule is still selected
			if (!ruleStillSelected($allSteps, stepName, rule)) continue;

			// ignore if exclude has already ran or rule doens't have exclude rules
			if (ruleType === 'excludeHasRun' && !ruleGroup[rule]?.exclude) continue;

			// runs all steps through current rule: exclude rule or all other rules
			$allSteps =
				ruleType === 'excludeHasRun'
					? await excludeRule({...ruleGroup[rule], ruleId: rule}, $allSteps, stepName)
					: await runningRule({...ruleGroup[rule], ruleId: rule}, $allSteps, stepName);

			// update excludeHasRun or ruleHasRun flag
			ruleGroup[rule][ruleType] = true;
		}
		return $allSteps;
	};

/** Checks if selected product is still selected */
const ruleStillSelected = (allSteps, stepName, rule) => {
	const step = allSteps.find((wfs) => wfs.name === stepName);
	return !!step.selected.filter((s) => s?.initial === rule)?.length;
};

/** Gets rules for each selected product in passed array */
const getRules = ($hcubeRules = {}, $wfs = []) =>
	new Promise((resolve) => {
		const $ruleKeys = Object.keys($hcubeRules);
		let allLiveRules = {};

		for (const wfs of $wfs) {
			if (!wfs.stepEnabled || wfs?.customContent) continue;

			// rules for eachStep
			const rulesForStep = {};

			// check each selected product for rule
			for (const prod of wfs.selected) {
				if ($ruleKeys.includes(prod.initial)) {
					// add rule list to step
					rulesForStep[prod.initial] = {...clonedeep($hcubeRules[`${prod.initial}`])};
				}
			}

			if (Object.keys(rulesForStep)?.length) {
				allLiveRules = {...allLiveRules, [wfs.name]: {...rulesForStep}};
			}
		}

		if (!Object.keys(allLiveRules)?.length) allLiveRules = null;
		resolve(allLiveRules);
	});

// #region running
/** Runs a rule through a given step  */
const runningRule = (rule, $wfs) =>
	new Promise((resolve) => {
		requiredRule(rule, $wfs)
			.then(($wfs1) => exclusiveRule(rule, $wfs1))
			.then(($wfs2) => suggestRule(rule, $wfs2))
			.then(($wfs3) => resolve($wfs3));
	});
// #endregion running

// #region required
/** Handles the requried rule */
const requiredRule = ({exclusive, require, ruleId}, $wfs) =>
	new Promise((resolve) => {
		// required rule
		const wfs = $wfs;

		if (require && !exclusive) {
			for (const step of wfs) {
				// ignore custom steps and steps without require products
				if (step?.customContent || !step.products.some((prod) => require.includes(+prod.key))) continue;

				for (const product of step.products) {
					if (require.includes(+product.key)) {
						// skip products that are excluded: *** exclude rule takes precedence
						if (product.rules.excludeBy.size > 0) continue;

						// add ruleId to product
						product.rules.requiredBy.add(ruleId);

						// add required products as selected to step
						if (!step.selected.find((sel) => sel.initial === product.key)) {
							// reset suggestProduct flag if avalible
							if (!isRestoredWorkflow) delete product.rules.suggestedRan;

							step.selected.push({
								initial: product.key,
								updated: product.key,
							});
						}
					}
				}
			}
		}
		resolve(wfs);
	});
// #endregion required

// #region exclusive
/** Handles the exclusive rule */
const exclusiveRule = ({exclusive, require, ruleId}, $wfs) =>
	new Promise((resolve) => {
		// exclusive rule
		const wfs = $wfs;

		if (exclusive && require?.length) {
			for (const step of wfs) {
				// ignore custom steps
				if (step?.customContent) continue;

				// remove any exclusive products from require array that are already excluded
				const requireList = require.filter(
					(key) => !!step.products.find((prod) => +prod.key === key && prod.rules.excludeBy.size === 0),
				);

				// ignore steps when no exclusive require products are found
				if (!requireList?.length || !step.products.some((prod) => requireList.includes(+prod.key))) continue;

				/** Adding Flags to Products and Adding to Selected */
				for (const product of step.products) {
					// skip products that are excluded: *** exclude rule takes precedence
					if (product.key.includes('custom-product') || product.rules.excludeBy.size > 0) continue;

					// add ruleId to product and set flag
					if (!requireList.includes(+product.key)) product.rules.exclusiveBy.add(ruleId);

					if (requireList.includes(+product.key)) {
						// add product to selected if not already selected
						product.rules.exclusiveRequiredBy.add(ruleId);

						if (!step.selected.find((sel) => sel.initial === product.key)) {
							// reset suggestProduct flag if avalible
							if (!isRestoredWorkflow) delete product.rules.suggestedRan;

							// add product to selected
							step.selected.push({initial: product.key, updated: product.key});
						}
					}
				}

				/** Removing products from selected */
				step.selected = step.selected.filter(({initial}) => {
					// keep custom products
					if (initial.includes('custom-product') || requireList.includes(+initial)) return true;

					// check if exclusiveRequiredBy products are still selected
					const prodToCheck = step.products.find((prod) => prod.key === initial).rules.exclusiveRequiredBy;

					if (prodToCheck.size > 0) {
						const stillSelected = [];

						for (const $step of wfs) {
							if (!$step.customContent) {
								for (const selected of $step.selected) {
									if (selected?.initial) stillSelected.push(selected.initial);
								}
							}
						}

						// keep if products are still selected
						if (stillSelected.some((selectedId) => prodToCheck.has(selectedId))) return true;
					}
					return false;
				});
			}
		}

		resolve(wfs);
	});
// #endregion exclusive

// #region exclude
/** Handles the exclude rule */
const excludeRule = ({exclude, ruleId}, $wfs) =>
	new Promise((resolve) => {
		// exclude rule
		const wfs = $wfs;
		if (exclude) {
			for (const step of wfs) {
				if (step?.customContent || !step.products.some((prod) => exclude.includes(+prod.key))) continue;

				// remove products from selected if they are excluded
				step.selected = step.selected.filter(
					(prod) => prod.initial.includes('custom-product') || !exclude.includes(+prod.initial),
				);

				// add ruleId to products that are not exclusive
				for (const product of step.products) {
					// add ruleId to product and set flag
					if (!product.key.includes('custom-product') && exclude.includes(+product.key))
						product.rules.excludeBy.add(ruleId);
				}
			}
		}

		resolve(wfs);
	});
// #endregion exclude

// #region suggest
/** Handles suggest rule */
const suggestRule = ({ruleId, suggest}, $wfs) =>
	new Promise((resolve) => {
		const suggestingProduct = $wfs.reduce(
			(prev, curr) => (curr?.products ? prev || curr?.products.find((prod) => prod.key === ruleId) : prev),
			undefined,
		);

		if (suggest) {
			for (const {selected, products, customContent} of $wfs) {
				// ignore custom step
				if (customContent) continue;

				for (const product of products) {
					if (!product?.rules) continue;

					const {
						key,
						rules: {excludeBy, exclusiveBy, suggestedBy},
					} = product;

					if (!suggest.includes(+key) || excludeBy.size > 0 || exclusiveBy.size > 0) continue;

					suggestedBy.add(ruleId);

					// add suggested product as selected to step
					if (!selected.find((sel) => sel.initial === key) && !suggestingProduct.rules?.suggestedRan)
						selected.push({initial: key, updated: key});
				}
			}
			suggestingProduct.rules.suggestedRan = new Set(['ran']);
		}
		resolve($wfs);
	});
// #endregion suggest

export {
	cleanWorkflowSteps,
	getRules,
	excludeRule,
	exclusiveRule,
	requiredRule,
	runningRule,
	runRulesForAllSteps,
	runRulesForWorkflow,
};
